import { useIsConfirmed } from '@client/hooks/use-confirm.hook';
import { useRef, useState } from 'react';
import { Box, InputBase, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Button } from '@ui/buttons/Button';
import { Icon } from '@ui/Icon';
import { usePhasesCardStyles } from './phases-card.styles';
import { createSavedScopeSchema } from '@shared/models/saved-scope.model';
import { getScope } from '@features/scope/scope.store';
import {
  createSavedScope,
  overwriteSavedScope,
  useSavedScopes,
} from '@features/lifecycles/saved-scopes.hooks';
import { savedScopeToPreset } from '@features/lifecycles/saved-scopes.helpers';
import { ScopePreset } from '@features/lifecycles/scope-presets.data';
import {
  getProject,
  useBuildingLifetime,
  useProjectId,
} from '@client/store/project/project.hook';
import { useResolvedProjectTarget } from '@features/scope/scope-targets.hooks';
import { getSelectedOrganization } from '@client/store/organization';

type SaveScopeFormProps = {
  context: 'project' | 'overview';
  onConfirmingChange: (confirming: boolean) => void;
  onSaved: (preset: ScopePreset, organization: string) => void;
};

/** Naming and saving stays inside the scope panel; failed saves keep the draft. */
export const SaveScopeForm = ({
  context,
  onSaved,
  onConfirmingChange,
}: SaveScopeFormProps) => {
  const confirm = useIsConfirmed();
  const { classes } = useStyles();
  const { classes: panelClasses, cx } = usePhasesCardStyles();
  const projectId = useProjectId();
  const { scopes, organization, canSave } = useSavedScopes();
  const lifetime = useBuildingLifetime();
  const target = useResolvedProjectTarget();
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const savingRef = useRef(false);
  if (!canSave) return null;

  const save = async () => {
    if (!organization || savingRef.current) return;
    const { lifecycles, sbefGroups, conservative } = getScope();
    const parsed = createSavedScopeSchema.safeParse({
      name,
      lifecycles,
      sbefGroups,
      conservative,
      ...(context === 'project'
        ? { calculationPeriodYears: lifetime, target }
        : {}),
    });
    if (!parsed.success) {
      setError('Enter a name (1–80 characters).');
      return;
    }
    const existing = scopes.find(
      (scope) =>
        !scope.deleted &&
        scope.name.toLowerCase() === parsed.data.name.toLowerCase(),
    );
    savingRef.current = true;
    setSaving(true);
    setError('');
    try {
      if (existing) {
        onConfirmingChange(true);
        let confirmed: boolean;
        try {
          confirmed = await confirm({
            title: 'A scope with this name already exists.',
            cancellationText: 'Cancel',
            confirmationText: 'Overwrite',
          });
        } finally {
          requestAnimationFrame(() => onConfirmingChange(false));
        }
        if (!confirmed) return;
      }
      const saved = existing
        ? await overwriteSavedScope(organization, existing.id, parsed.data)
        : await createSavedScope(organization, parsed.data);
      if (
        getSelectedOrganization() !== organization ||
        (context === 'project' && getProject().id !== projectId)
      )
        return;
      onSaved(savedScopeToPreset(saved), organization);
      setName('');
    } catch {
      setError('Could not save scope. Try again or choose another name.');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box
        component="form"
        className={cx(panelClasses.presetOption, classes.row)}
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <Box
          component="button"
          type="submit"
          className={classes.addButton}
          aria-label="Create scope"
          disabled={saving || !name.trim()}
        >
          <Icon category="mui" icon="Add" size={18} />
        </Box>
        <InputBase
          className={classes.input}
          placeholder="New scope"
          value={name}
          disabled={saving}
          endAdornment={
            <span
              className={cx(
                classes.saveSlot,
                !name.trim() && classes.saveHidden,
              )}
            >
              <Button
                type="submit"
                size="tiny"
                disabled={saving || !name.trim()}
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </span>
          }
          inputProps={{ 'aria-label': 'New scope', maxLength: 80 }}
          onChange={(event) => {
            setName(event.target.value);
            setError('');
          }}
          onKeyDown={(event) => {
            if (event.key !== 'Escape') return;
            event.stopPropagation();
            setName('');
            setError('');
          }}
        />
      </Box>
      {error && (
        <Typography className={classes.error} role="alert">
          {error}
        </Typography>
      )}
    </Box>
  );
};

const useStyles = makeStyles()(() => ({
  row: { cursor: 'default', '&&': { paddingRight: 2 } },
  saveSlot: {
    display: 'flex',
    justifyContent: 'flex-end',
    flexShrink: 0,
    width: 64,
  },
  saveHidden: { visibility: 'hidden' },
  addButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 34,
    height: 32,
    padding: '0 8px',
    border: 0,
    backgroundColor: 'transparent',
    color: 'inherit',
    cursor: 'pointer',
    '&:disabled': { cursor: 'default', opacity: 0.4 },
  },
  input: {
    flex: 1,
    minWidth: 0,
    fontSize: 13,
    color: 'inherit',
  },
  error: {
    maxWidth: 300,
    padding: '4px 8px 4px 34px',
    fontSize: 12,
    color: '#b00020',
  },
}));
